'use client';

import React, { Suspense, useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X } from 'lucide-react';
import { NotificationBellCenter } from '@/components/layout/NotificationBellCenter';
import { UserNav } from '@/components/layout/UserNav';
import { WorkspaceSwitcher } from '@/components/layout/WorkspaceSwitcher';
import { hideOnScrollNavClass, useHideOnScroll } from '@/hooks/useHideOnScroll';

type DashboardShellProps = {
  sidebar: React.ReactNode;
  children: React.ReactNode;
  shellClassName?: string;
  mainPadding?: string;
  showMobileNav?: boolean;
  showHeader?: boolean;
  density?: 'default' | 'participant' | 'jury';
  fullWidth?: boolean;
};

export function DashboardShell({
  sidebar,
  children,
  shellClassName = 'bg-[#f7f3e9]',
  mainPadding = 'p-4 sm:p-8 lg:p-12 pt-6 lg:pt-8',
  showMobileNav = true,
  showHeader = true,
  density = 'default',
  fullWidth = false,
}: DashboardShellProps) {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerHidden = useHideOnScroll({ enabled: showHeader });
  const isParticipant = density === 'participant';
  const isJury = density === 'jury';
  const sidebarWidth = 'w-[280px]';
  const mainOffset = 'md:ml-[280px]';

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <div className={`flex min-h-screen ${shellClassName}`}>
      <div className={`hidden md:block ${sidebarWidth} shrink-0 fixed inset-y-0 left-0 z-50 h-screen`}>{sidebar}</div>

      {showMobileNav && mobileOpen && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="md:hidden fixed inset-0 z-[60] bg-black/40 transition-opacity duration-200"
            onClick={() => setMobileOpen(false)}
          />
          <div className="md:hidden fixed inset-y-0 left-0 z-[70] w-[min(280px,88vw)] shadow-lg animate-in slide-in-from-left duration-200">
            <div className="relative h-full">
              {sidebar}
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
                className="absolute top-4 right-3 p-2 rounded-lg bg-bg/10 text-text-inverse hover:bg-bg/20"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </>
      )}

      <main className={`relative flex-1 min-h-screen overflow-x-hidden flex flex-col ${mainOffset} w-full`}>
        {showHeader && (
          <header
            className={`shrink-0 sticky top-0 z-40 flex items-center justify-between gap-3 border-b ${hideOnScrollNavClass(headerHidden)} ${
              isParticipant || isJury
                ? 'h-14 bg-[var(--color-bg)] border-[var(--color-border)] px-4 sm:px-5 lg:px-8'
                : 'h-16 bg-[#f7f3e9]/95 backdrop-blur-md border-black/[0.06] px-4 sm:px-8 lg:px-12 md:h-20'
            }`}
            aria-hidden={headerHidden}
          >
            {showMobileNav ? (
              <button
                type="button"
                aria-label="Open menu"
                onClick={() => setMobileOpen(true)}
                className="md:hidden p-2 rounded-lg hover:bg-black/5 text-[var(--color-text)] transition-colors duration-150"
              >
                <Menu className="w-5 h-5" />
              </button>
            ) : (
              <span className="md:hidden w-10" />
            )}
            <div className={`ml-auto flex items-center ${isParticipant || isJury ? 'gap-2 sm:gap-3' : 'gap-3'}`}>
              <NotificationBellCenter />
              <UserNav />
            </div>
          </header>
        )}

        <div
          key={pathname}
          className={`relative flex-1 w-full mx-auto ${fullWidth ? 'max-w-none' : isParticipant ? 'max-w-[72rem]' : 'max-w-[1400px]'} ${mainPadding}`}
        >
          {children}
        </div>
      </main>
    </div>
  );
}
